import { chalkText, chalkLine } from '../../primitives/chalk';

function drawLabelBadge(ctx, text, x, y, size) {
  const safeText = String(text || '');
  if (!safeText) return;

  ctx.save();
  ctx.font = `${size}px sans-serif`;
  ctx.textAlign = 'center';
  const textWidth = Math.max(10, ctx.measureText(safeText).width);
  const padX = 8;
  const padY = 5;
  const width = textWidth + padX * 2;
  const height = size + padY * 2;
  const rectX = x - width / 2;
  const rectY = y - size * 0.8 - padY;

  ctx.fillStyle = 'rgba(10, 20, 32, 0.86)';
  ctx.strokeStyle = 'rgba(86, 182, 255, 0.64)';
  ctx.lineWidth = 1.25;
  ctx.fillRect(rectX, rectY, width, height);
  ctx.strokeRect(rectX, rectY, width, height);
  ctx.restore();

  chalkText(ctx, safeText, x, y, size, 0.98, 'sans-serif', 'center', {
    role: 'diagramLabel',
  });
}

export async function prepareDiagramStrokeNode(env, spec) {
  const area = env.boardArea();
  const fontSize = env.getStyle('bodySize');
  const titleSize = spec.title ? env.getStyle('bodySize') : 0;

  const nodeRadius = 25;
  const hGap = area.w * spec.diagram.layout.hGapK;
  const vGap = area.h * spec.diagram.layout.vGapK;

  const nodePositions = new Map();

  if (spec.diagram.layout.mode === 'tree' && spec.diagram.layout.rootId) {
    const levels = new Map();
    const childrenMap = new Map();

    for (const edge of spec.diagram.edges) {
      if (!childrenMap.has(edge.from)) {
        childrenMap.set(edge.from, []);
      }
      childrenMap.get(edge.from).push(edge.to);
    }

    const assignLevels = (nodeId, level) => {
      levels.set(nodeId, level);
      const children = childrenMap.get(nodeId) || [];
      for (const child of children) {
        assignLevels(child, level + 1);
      }
    };

    assignLevels(spec.diagram.layout.rootId, 0);

    const levelNodes = new Map();
    for (const [nodeId, level] of levels.entries()) {
      if (!levelNodes.has(level)) {
        levelNodes.set(level, []);
      }
      levelNodes.get(level).push(nodeId);
    }

    const startY = area.y + 50;

    for (const [level, nodes] of levelNodes.entries()) {
      const y = startY + level * vGap;
      const totalWidth = (nodes.length - 1) * hGap;
      const startX = area.x + (area.w - totalWidth) / 2;

      nodes.forEach((nodeId, i) => {
        nodePositions.set(nodeId, {
          x: startX + i * hGap,
          y,
        });
      });
    }
  } else {
    spec.diagram.nodes.forEach((node, i) => {
      const x = area.x + (i % 3) * hGap + area.w * 0.2;
      const y = area.y + Math.floor(i / 3) * vGap + area.h * 0.2;
      nodePositions.set(node.id, { x, y });
    });
  }

  const nodes = spec.diagram.nodes.map((node) => {
    const pos = nodePositions.get(node.id) || { x: area.x + area.w / 2, y: area.y + area.h / 2 };
    return {
      id: node.id,
      label: node.label,
      x: pos.x,
      y: pos.y,
      radius: nodeRadius,
    };
  });

  const edges = spec.diagram.edges.map((edge) => {
    const fromNode = nodes.find((n) => n.id === edge.from);
    const toNode = nodes.find((n) => n.id === edge.to);
    return {
      from: edge.from,
      to: edge.to,
      x1: fromNode?.x || 0,
      y1: fromNode?.y || 0,
      x2: toNode?.x || 0,
      y2: toNode?.y || 0,
      label: edge.label,
    };
  });

  const children = [];
  edges.forEach((edge) => {
    children.push({
      type: 'edge',
      duration: spec.stroke.secPerEdge,
      edge,
    });
  });
  nodes.forEach((node) => {
    children.push({
      type: 'node',
      duration: spec.stroke.secPerNode,
      node,
    });
  });

  return {
    area,
    titleText: spec.title?.text,
    titleX: area.x + area.w / 2,
    titleY: area.y + 20,
    titleSize,
    nodes,
    edges,
    children,
    fontSize,
  };
}

export function commitDiagramStrokeNode(packet, ctxs, anim) {
  let t = 0;
  const labelPlacementByNodeId = new Map();

  anim.add((dt) => {
    t += dt;

    ctxs.ink.clearRect(packet.area.x, packet.area.y, packet.area.w, packet.area.h);

    if (packet.titleText) {
      chalkText(
        ctxs.ink,
        packet.titleText,
        packet.titleX,
        packet.titleY,
        packet.titleSize,
        0.9,
        'sans-serif',
        'center',
        { role: 'diagramTitle' }
      );
    }

    let currentTime = 0;
    let elementsDrawn = 0;

    for (const child of packet.children) {
      const duration = child.duration;

      if (t >= currentTime) {
        const progress = Math.min((t - currentTime) / duration, 1);

        if (child.type === 'edge') {
          const edge = child.edge;
          const x2 = edge.x1 + (edge.x2 - edge.x1) * progress;
          const y2 = edge.y1 + (edge.y2 - edge.y1) * progress;
          chalkLine(ctxs.ink, edge.x1, edge.y1, x2, y2, 2, 0.8, { role: 'diagramEdge' });
        } else {
          const node = child.node;
          ctxs.ink.save();
          ctxs.ink.strokeStyle = 'rgba(245, 250, 255, 0.9)';
          ctxs.ink.lineWidth = 2;
          ctxs.ink.beginPath();
          ctxs.ink.arc(node.x, node.y, node.radius, 0, Math.PI * 2 * progress);
          ctxs.ink.stroke();
          ctxs.ink.restore();

          if (progress >= 1) {
            let labelPlacement = labelPlacementByNodeId.get(node.id);
            if (!labelPlacement) {
              const labelWidth = Math.max(24, ctxs.ink.measureText(node.label).width + 10);
              const labelHeight = Math.max(14, packet.fontSize * 0.85 + 8);
              const candidates = [
                { x: node.x - labelWidth / 2, y: node.y + node.radius + 6 },
                { x: node.x - labelWidth / 2, y: node.y - node.radius - labelHeight - 6 },
                { x: node.x + node.radius + 6, y: node.y - labelHeight / 2 },
                { x: node.x - node.radius - labelWidth - 6, y: node.y - labelHeight / 2 },
              ];

              if (ctxs.interaction?.findLeastOccupiedRect) {
                labelPlacement = ctxs.interaction.findLeastOccupiedRect(
                  candidates,
                  { w: labelWidth, h: labelHeight },
                  { sampleStep: 2, alphaThreshold: 14, earlyExitCoverage: 0.002 }
                );
              } else {
                labelPlacement = {
                  x: candidates[0].x,
                  y: candidates[0].y,
                  w: labelWidth,
                  h: labelHeight,
                };
              }

              if (!labelPlacement) {
                labelPlacement = {
                  x: candidates[0].x,
                  y: candidates[0].y,
                  w: labelWidth,
                  h: labelHeight,
                };
              }
              labelPlacementByNodeId.set(node.id, labelPlacement);
            }

            const labelX = labelPlacement.x + labelPlacement.w / 2;
            const labelY = labelPlacement.y + labelPlacement.h * 0.72;
            drawLabelBadge(ctxs.ink, node.label, labelX, labelY, packet.fontSize * 0.68);
          }
        }

        if (progress >= 1) {
          elementsDrawn++;
        }
      }

      currentTime += duration;
    }

    return elementsDrawn < packet.children.length;
  });
}
